import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { appDispatchAsyncThunk } from 'state'
import createPeriodicRefresher from 'utils/createPeriodicRefresher'
import { getAccount } from 'state/web3/account'
import { transformBarData, transformBoostData, transformBoostPoolData } from 'state/xVvs/helpers'
import { DeserializedBarData, DeserializedBoostData, State } from 'state/types'
import {
  fetchVvsBarPoolDataAsync,
  fetchVvsBarUserDataAsync,
  fetchVvsBoostPoolDataAsync,
  fetchVvsBoostUserDataAsync,
} from '.'

export const usePeriodicRefreshUserVvsBar = createPeriodicRefresher(
  async () => {
    const account = getAccount()

    await appDispatchAsyncThunk(fetchVvsBarPoolDataAsync())
    if (account) {
      await appDispatchAsyncThunk(fetchVvsBarUserDataAsync(account))
    }
  },
  {
    frequency: 'fast',
    dbgName: 'PollUserVvsBar',
  },
)

export const usePeriodicRefreshUserVvsBoost = createPeriodicRefresher(
  async () => {
    const account = getAccount()
    if (!account) return

    await appDispatchAsyncThunk(fetchVvsBoostUserDataAsync(account))
  },
  {
    frequency: 'fast',
    dbgName: 'PollUserVvsBoost',
  },
)

export const usePollVvsBoostPoolData = createPeriodicRefresher(
  async () => {
    await appDispatchAsyncThunk(fetchVvsBoostPoolDataAsync())
  },
  {
    frequency: 'fast',
    dbgName: 'PollVvsBoostPoolData',
  },
)

export const usePollUserVvsBar = (_account: string) => {
  usePeriodicRefreshUserVvsBar()
}

export const useUserVvsBar = (): { barData: DeserializedBarData; userDataLoaded: boolean } => {
  const { barData, userDataLoaded } = useSelector((state: State) => ({
    barData: state.xVvs.barData,
    userDataLoaded: state.xVvs.userDataLoaded,
  }))
  return { barData: transformBarData(barData), userDataLoaded }
}

export const usePollUserVvsBoost = (account: string) => {
  usePeriodicRefreshUserVvsBoost(Boolean(account))
}

export const useUserVvsBoost = (): {
  boostData: DeserializedBoostData
  userDataLoaded: boolean
} => {
  const { boostData, userDataLoaded } = useSelector((state: State) => {
    return {
      boostData: state.xVvs.boostData,
      userDataLoaded: state.xVvs.userDataLoaded,
    }
  })
  return { boostData: transformBoostData(boostData), userDataLoaded }
}

export const usePollVvsBoostPoolDataOnce = () => {
  useEffect(() => {
    usePollVvsBoostPoolData.untilRefreshedOnce()
  }, [])
}

export const useVvsBoostPool = () => {
  const { boostPoolData } = useSelector((state: State) => ({
    boostPoolData: state.xVvs.boostPoolData,
  }))
  return { boostPoolData: transformBoostPoolData(boostPoolData) }
}
